import { createContext, use, type ReactNode } from 'react'

import { authClient } from '@/features/_lib/auth-client'
import type { AuthUser } from '@/features/_lib/auth-client.types'

interface SessionData {
  user: AuthUser
  session: {
    id: string
    userId: string
    token: string
    expiresAt: Date
  }
}

export interface SessionContextValue {
  data: SessionData | null
  isPending: boolean
  error: Error | null
  status: 'loading' | 'authenticated' | 'unauthenticated'
}

type RawSession = ReturnType<typeof authClient.useSession>

export function transformRawSession(raw: RawSession): SessionContextValue {
  return {
    data: raw.data
      ? {
          user: raw.data.user as AuthUser,
          session: raw.data.session,
        }
      : null,
    isPending: raw.isPending,
    error: raw.error,
    status: raw.isPending ? 'loading' : raw.data ? 'authenticated' : 'unauthenticated',
  }
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const value = transformRawSession(authClient.useSession())

  return <SessionContext value={value}>{children}</SessionContext>
}

export function useSessionContext(): SessionContextValue {
  const context = use(SessionContext)

  if (!context) {
    throw new Error('useSessionContext must be used within a SessionProvider')
  }

  return context
}
